import { useState, useCallback } from 'react';
import { Box, Stack } from '@mui/material';
import { Button, Typography, colors } from '@sweep-io/sweep-design';
import { generateAgentChatUrl } from './helper';
import { InfoDialog } from '../common/dialogs/InfoDialog';
import { TruncatedTextTooltip } from '../common/TruncatedTextTooltip';
import useSendBiEvent from '../../hooks/useSendBiEvent';
import { AI_AGENTS_EVENTS } from '../../services/events';
import { telemetry } from '../../telemetry';
import { selectUserAccountId } from '../../reducers/userInfoReducer';
import { useSelector } from 'react-redux';

export type ShareChatDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  chatId: string;
  crmOrgId?: string;
};

export const ShareChatDialog = ({
  isOpen,
  onClose,
  agentId,
  chatId,
  crmOrgId,
}: ShareChatDialogProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const sendBiEvent = useSendBiEvent();
  const userAccountId = useSelector(selectUserAccountId);
  const shareableUrl = generateAgentChatUrl({
    accountId: userAccountId ?? '',
    agentId,
    chatId,
    crmOrgId,
    isShareable: true,
    includeOrigin: true,
  });

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setIsCopied(true);

      sendBiEvent({
        name: AI_AGENTS_EVENTS.shareChatCopyLink,
        props: {
          agentId,
          chatId,
        },
      });

      setTimeout(() => {
        setIsCopied(false);
      }, 5000);
    } catch (error) {
      telemetry.captureError(new Error('Failed to copy to clipboard'), error);
    }
  }, [shareableUrl, sendBiEvent, agentId, chatId]);

  return (
    <InfoDialog
      open={isOpen}
      handleClose={onClose}
      onClose={onClose}
      showCloseButton
      closeButtonSize="tiny"
      titleJsx="Share chat with your team"
      titleTypographyVariant="h3"
      PaperPropsSx={{ width: '448px' }}
    >
      <Stack gap="16px" mt={1} mb={2}>
        <Typography variant="body" color={colors.grey[700]}>
          Anyone in your Sweep account can view this chat using the link below.
        </Typography>

        <Box
          sx={{
            border: `1px solid ${colors.grey[300]}`,
            borderRadius: '8px',
            padding: '11px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <TruncatedTextTooltip variant="h4" color={colors.grey[900]}>
            {shareableUrl}
          </TruncatedTextTooltip>

          <Box minWidth="fit-content">
            <Button
              variant="primary"
              startIconName={isCopied ? 'Copy' : 'Link'}
              onClick={handleCopyLink}
              size="small"
            >
              {isCopied ? 'Copied' : 'Copy link'}
            </Button>
          </Box>
        </Box>
      </Stack>
    </InfoDialog>
  );
};
